"use client";

import { c as _c } from "react/compiler-runtime";
import { type MagnusUIMessage, MULTIPLE_CHOICE_SKIP_MESSAGE, type TurnOptions } from "@bg-bgi/magnus-sdk";
import { ClientToolProvider, type LiveCallChatSubmit, type MagnusLiveCallSource, resolveToolRegistry, sendAccepted, type ToolUIRegistry, type UseMagnusResult, useLiveCallChatBridge, useMagnus, useMagnusAttachmentStatuses, useMagnusClient, useMagnusTools } from "@bg-bgi/magnus-sdk/react";
import { cn } from "@workspace/ui/lib/utils";
import { type ReactNode, type Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AttachmentPreviewDialog, AttachmentPreviewProvider, type AttachmentPreviewTarget, isPreviewableMediaType } from "./attachment-preview";
import { ChicletChatActionsProvider, type ChicletRegistry, ChicletRegistryProvider } from "./chiclets";
import { type ArtifactMentionOptions, type ArtifactPickerItem, type ComposerAttachment, type ComposerDictationSource, type ComposerModelsSource, type ComposerUploadsSource, type EditorPart, type FollowUpQueueResult, partsToPromptString, RichComposer, type RichComposerHandle, type RichComposerRightClusterContext, type SkillMentionOptions, type SkillPickerItem, toAttachmentFilePart, useAttachments } from "./composer";
import { FollowUpQueue } from "./composer/message-queue";
import { beginFollowUpEdit, enqueueFollowUp, getFollowUpEditSlot, insertFollowUpAt, mergeFollowUpParts, takeFollowUp, takeFollowUpEditSlot, tryLockDrain, unlockDrain, unshiftFollowUp, useDrainLocked, useFollowUpEditSlot, useFollowUpQueue } from "./composer/message-queue-store";
import { ApprovalInput } from "./composer/prompts/approval-input";
import type { ChatApprovalsSource } from "./composer/prompts/approval-prompt";
import { MultipleChoiceInput } from "./composer/prompts/multiple-choice-input";
import { getActiveMultipleChoice } from "./composer/prompts/multiple-choice-prompt";
import { useApprovalPrompt } from "./composer/prompts/use-approval-prompt";
import { DEFAULT_TOOL_VIEWS } from "./default-tool-views";
import type { ChatFeedbackSource } from "./feedback";
import { type ChatHistorySource, ChatHistorySwitcher } from "./history-switcher";
import { type FetchToolOutput, LazyToolOutputProvider } from "./lazy-tool-output";
import { LiveCallPill } from "./live-call";
import type { MagnusChatMessagePart, MagnusChatProcessingSnapshot } from "./message";
import { renderUserMessageTimestamp } from "./message-timestamp";
import { ChatHydrationSkeleton } from "./skeleton";
import { ToolViewsProvider } from "./tool-views";
import { ToolViewProvider } from "./tools/_framework/tool-view-provider";
import { type MagnusChatLinkComponent, Transcript } from "./transcript";
import { TransportErrorBanner, useTransportErrorBanner } from "./transport-error-banner";
import { useElementHeight } from "./use-element-height";

/** Context handed to a function `emptyState` (the `renderEmpty` idiom). */
export interface MagnusChatEmptyContext {
  /** The block-built composer, ready to drop into a hero layout. */
  composer: ReactNode;
  messages: MagnusUIMessage[];
  /** Send a plain-text turn (e.g. from a prompt-suggestion chip). */
  sendText: (text: string) => void;
  status: UseMagnusResult["status"];
}

/** Submit handler. Returns/resolves false to keep the draft (retry). */
export type MagnusChatSubmit = (parts: EditorPart[]) => boolean | Promise<boolean>;

/**
 * Grouped composer host slots (gap-audit C1–C18), keeping the top-level
 * `MagnusChatProps` surface flat. Every field is optional; each absent
 * field degrades to the plain composer.
 */
export interface MagnusChatComposerConfig {
  /** Host menu content inside the block-owned "+" actions menu, below
   *  "Attach files" (C12 — capability toggles, connectors items). */
  actionsMenuExtras?: ReactNode;
  /**
   * Focus the editor on mount (C2). Default is branch-aware: the empty
   * hero's composer focuses, the docked active composer doesn't (so it can't
   * yank focus off a transcript the user is reading). Pass `true` for the
   * old always-focus behavior.
   */
  autoFocus?: boolean;
  /** Extra content between the attachment strip and the form (e.g. the
   *  workspace/context chip row). */
  extras?: ReactNode;
  /** Rendered inside the composer frame, above the editor. */
  headerSlot?: ReactNode;
  /** Seed a skill mention on mount (C4 — skill-start chiclet deep link). */
  initialSkill?: SkillPickerItem | null;
  /** Rendered in the composer footer's left cluster, beside the "+" menu. */
  leftTools?: ReactNode;
  /** Bell mounted beside the model selector (C13 — NotifyWhenDone). */
  notifyBell?: ReactNode;
  /** Staged-tile activation (C11 — the host opens its preview modal).
   *  Absent → the block's lightweight image/PDF preview of the local file. */
  onAttachmentActivate?: (attachment: ComposerAttachment) => void;
  /** Fires when a staged attachment's processing watch reports terminal
   *  failure (ADR-055 complement — the host toasts / replaces text). */
  onAttachmentProcessingFailed?: (uploadId: string, fileName: string) => void;
  /** Fires when a staged attachment's processing watch reports any
   *  terminal transition (ADR-055 complement — sound/notify parity). */
  onAttachmentProcessingSettled?: (uploadId: string) => void;
  /** Fires with the skill IDs currently present as mentions (C5). */
  onSkillsChange?: (ids: string[]) => void;
  /** Fires on every content change with the canonical prompt string
   *  (C1). Pair with `value` for a controlled draft. */
  onValueChange?: (value: string) => void;
  /**
   * When set (with `renderPromptReplacement`), the composer is replaced
   *  by the rendered replacement (C17). `kind`/`payload` are opaque host
   *  values; the host detects and renders. The block already renders the
   *  approval card (with `approvals`) and the multiple-choice picker
   *  itself; a host replacement wins over both.
   */
  promptReplacement?: {
    kind: string;
    payload: unknown;
  } | null;
  /** Renders the C17 replacement. Receives the opaque host payload. */
  renderPromptReplacement?: (kind: string, payload: unknown) => ReactNode;
  /** Replaces the default Send/Stop button (C14 — voice cluster). */
  renderRightCluster?: (ctx: RichComposerRightClusterContext) => ReactNode;
  /** Wraps skill chips with a hover card. Absent → plain chips. */
  renderSkillHover?: (skillId: string, chip: ReactNode) => ReactNode;
  /** Rendered in the composer footer's right cluster, immediately before the
   *  model selector and ungapped from it (e.g. a context/usage meter). */
  rightTools?: ReactNode;
  /**
   * Extra send gate layered over the block's own (C8 — the flagship's
   * overfilled-context hold). `reason` becomes the send button's title.
   */
  submitDisabled?: {
    disabled: boolean;
    reason?: string;
  };
  /** Controlled draft value (canonical prompt string — C1). Omit for an
   *  uncontrolled editor. */
  value?: string;
}
export interface MagnusChatProps {
  /** Resume an in-flight stream on mount (self-driving mode). */
  activeStreamId?: string;
  /**
   * Resolves HITL approval pauses (ADR-050): a `data-approval-request`
   * part replaces the composer with the approval card, and the decision
   * goes through `submit`. Absent → no card; a paused tool call waits out
   * the server's auto-deny timeout. `useMagnusConversation` binds this.
   */
  approvals?: ChatApprovalsSource;
  artifactMentionOptions?: ArtifactMentionOptions;
  /** Artifacts offered by the "@" picker. Absent → no artifact mentions. */
  artifacts?: {
    items: ArtifactPickerItem[];
    loading?: boolean;
  };
  /** Live processing snapshots for sent attachments, keyed by upload id.
   *  Absent → the block watches sent attachments itself through
   *  `uploads.watchProcessing` (and retries through
   *  `uploads.retryProcessing`); with neither, tiles show the send-time
   *  status snapshot only. */
  attachmentStatuses?: ReadonlyMap<string, MagnusChatProcessingSnapshot>;
  /** Rendered at the top of the transcript (e.g. a preparing-files
   *  notice). */
  banner?: ReactNode;
  /**
   * The chat id. Optional in self-driving mode (the block creates a chat
   * on mount) — and in controlled mode, where it's only used for display
   * wiring that needs an id and falls back to a fixed placeholder. Note the
   * block does NOT remount on a controlled-mode `chatId` change: key the
   * element by chat id if you switch chats in place, or per-chat UI state
   * (scroll position, staged attachments, the draft) carries over.
   */
  chatId?: string;
  /** Surface-specific chiclets, merged over (and overriding) the defaults. */
  chiclets?: ChicletRegistry;
  /** Extra content inside the active conversation container (fabs etc.). */
  children?: ReactNode;
  className?: string;
  /** Grouped composer host slots (C1–C18). Absent → plain composer. */
  composer?: MagnusChatComposerConfig;
  /** Extra classes on the docked composer container (C18 — the floating
   *  widget's compact dock). */
  composerDockClassName?: string;
  /** Dock the composer over the transcript with measured bottom padding
   *  (the flagship layout). Default false = the composer's normal-flow
   *  layout (`/demo`, embeds). */
  composerDocked?: boolean;
  /** Editor handle (C16 — focus/setContent/insertText for voice, chiclets,
   *  and draft-hydration hooks). */
  composerRef?: Ref<RichComposerHandle | null>;
  /**
   * Tap-to-toggle dictation source (plan 027). Present → a mic button in
   * the composer's right cluster; finalized transcripts insert at the
   * cursor. Absent → no mic button. `useMagnusDictation()` returns a
   * ready-made source. Wins over `composer.renderRightCluster` when both
   * are supplied (the private layer never passes both).
   */
  dictation?: ComposerDictationSource;
  /** Small print under the docked composer (active branch). */
  disclaimer?: ReactNode;
  /**
   * Empty surface. A static node renders centered in the transcript area
   * (today's behavior); a function receives the composer node + helpers
   * and owns the whole empty layout (the flagship's hero).
   */
  emptyState?: ReactNode | ((ctx: MagnusChatEmptyContext) => ReactNode);
  /**
   * Queue follow-ups while a turn is in flight and drain them when the turn
   * is idle. Default true. Pass false for the old no-op-while-busy composer —
   * worth it on surfaces too short to spare the queue's rows above the input.
   */
  enableMessageQueue?: boolean;
  /**
   * The streaming engine, created by the host (controlled mode). Lets the
   * app layer voice/TTS/orchestration on top; the block skips its internal
   * engine, chat creation, and client-tool provider (tools arrive with the
   * engine). Omit to let `<MagnusChat>` create and own the engine.
   */
  engine?: UseMagnusResult;
  /** Thumbs/modal feedback source. Absent → no feedback affordances. */
  feedback?: ChatFeedbackSource;
  /**
   * Resolve a lazy tool-output placeholder (`@magnus/contract/lazy-tool-output`)
   * to the full output by tool-call id. Required when the host's chat read
   * opted into `lazyToolOutputs`; absent → placeholders render as generic rows.
   * Only the host tRPC `chat.get` can serve placeholders today; transcripts
   * read through the SDK REST surface (`/api/sdk/v1`) always carry full
   * outputs, so hosts on it can leave this unset.
   */
  fetchToolOutput?: FetchToolOutput;
  /** True while an auto-send is pending — keeps the empty branch from
   *  flashing before the first optimistic message lands. */
  hasPendingInitialSend?: boolean;
  /** Active-conversation header, rendered above the transcript. */
  header?: ReactNode;
  /** Previous-conversations switcher source. Absent → no switcher. */
  history?: ChatHistorySource;
  /** Hydrate a reopened chat's transcript (e.g. from `chats.get`) —
   *  self-driving mode only; a controlled engine is hydrated by its owner. */
  initialMessages?: MagnusUIMessage[];
  /** Routes the transcript's in-app links (e.g. Next `<Link>`). Absent →
   *  plain `<a>`, a full page load. */
  linkComponent?: MagnusChatLinkComponent;
  /**
   * Live voice call source (plan 042). Present → a call button in Send's
   * place while the composer is empty, a call pill above the composer, and
   * this chat registered as the call's delegation target: spoken requests
   * become user turns tagged with the call's `voiceSessionId`, and the
   * recap lands as a voice-session divider. Delegated turns go straight to
   * the engine, not through `onSubmit`. Absent → exactly today's layout.
   * `useMagnusConversation().chatProps.liveCall` returns a ready-made
   * source.
   */
  liveCall?: MagnusLiveCallSource;
  /** False when the host draws the call pill itself (e.g. one pill that
   *  follows the call across pages). Default true. */
  liveCallPill?: boolean;
  /** Hold queued sends while host work outside the engine status is pending. */
  messageQueuePaused?: boolean;
  /** Composer model-picker source. Absent → no model button. */
  models?: ComposerModelsSource;
  /** Sent-attachment click handler (e.g. a preview modal). Absent → the
   *  block's lightweight preview for images and PDFs (document parts via
   *  `uploads.resolveUrl`), open-in-a-new-tab for other files. */
  onAttachmentActivate?: (part: MagnusChatMessagePart) => void;
  /** Fired when a turn finishes streaming (e.g. auto-title invalidation) —
   *  self-driving mode only (a controlled engine reports its own lifecycle). */
  onFinish?: () => void;
  /** Fired once when a brand-new chat starts streaming (e.g. list refresh) —
   *  self-driving mode only. */
  onFirstStream?: () => void;
  /** Retries tier-2 processing on a failed sent attachment. Defaults to
   *  `uploads.retryProcessing` when the block is watching sent attachments
   *  itself (no host `attachmentStatuses`). */
  onRetryProcessing?: (uploadId: string) => void;
  /** Persist partial messages when a turn is stopped (self-driving mode). */
  onStop?: (partialMessages: MagnusUIMessage[]) => unknown;
  /**
   * Submit handler replacing the default `engine.sendMessage` send.
   * Returns/resolves false to keep the draft (e.g. a declined send).
   */
  onSubmit?: MagnusChatSubmit;
  placeholder?: string;
  /** Marks the current viewer as a non-owner (read-only shared chat):
   *  composer disabled with shared-chat placeholder, chiclet sends gated. */
  readOnly?: boolean;
  /** Explain a temporary or project-level input lock without calling it shared. */
  readOnlyPlaceholder?: string;
  /** Transport-error renderer mounted at the bottom of the transcript.
   *  Defaults to {@link TransportErrorBanner} (tiered copy, dismissible,
   *  hidden once the last assistant turn persists its own error). */
  renderError?: (error: Error) => ReactNode;
  /** Per-message slot rendered beneath each bubble (dev tooling). Defaults
   *  to {@link MessageErrorIndicator} on assistant turns persisted with
   *  `status: "error"`; its "Report error" goes through `feedback`. A host
   *  renderer replaces the default — compose the indicator in to keep it. */
  renderMessageDebug?: (message: MagnusUIMessage) => ReactNode;
  /** Mounted above each message row (surface-switch dividers/tags). */
  renderMessageHeader?: (message: MagnusUIMessage, prevMessage?: MagnusUIMessage) => ReactNode;
  /** Caption at the top of each assistant row's "…" menu (e.g. the model
   *  that produced the turn). */
  renderMessageMenuLabel?: (message: MagnusUIMessage) => ReactNode;
  /** Mounted at the head of each hover action row. Defaults to
   *  {@link renderUserMessageTimestamp} (a timestamp on user rows). */
  renderMessageMeta?: (message: MagnusUIMessage) => ReactNode;
  /** Optional display-only body for app-generated user requests. Null keeps the normal body. */
  renderUserMessage?: (message: MagnusUIMessage) => ReactNode;
  /** Overrides the plain-text send used by chiclets + the empty context. */
  send?: (text: string) => void;
  skillMentionOptions?: SkillMentionOptions;
  /** Skills offered by the "/" picker. Absent → no skill mentions. */
  skills?: {
    items: SkillPickerItem[];
    loading?: boolean;
  };
  /** Host-registered server-tool views, merged under the client-tool
   *  registry (e.g. the flagship's built-in registry) and over the block's
   *  own published views (multiple choice, ...). */
  toolViews?: ToolUIRegistry;
  /** Per-turn request options (model, workspace, ...), read at send time —
   *  self-driving mode only; a controlled engine carries its own. */
  turnOptions?: TurnOptions;
  /**
   * Composer attachments source. Absent → no attach button, no drag/drop,
   * pastes stay inline. Bind it to the same chat id you pass as `chatId` —
   * `useMagnusUploads(chatId)` does this (and `useMagnusConversation`
   * already spreads it). When `chatId` is omitted the block creates its
   * own chat on mount, so hosts using `uploads` should pass `chatId` too.
   */
  uploads?: ComposerUploadsSource;
}

/**
 * A complete Magnus chat screen: transcript + composer, wired to the
 * client from the surrounding `<MagnusProvider>`. When no `chatId` is
 * given, the block creates one via `magnus.chats.create()` on mount.
 * Data-backed affordances (model picker, feedback thumbs, history
 * switcher, attachments) appear only when the host injects their source
 * props.
 *
 * **Self-driving (default):** omit `engine` and the block creates and owns
 * the conversation engine. **Controlled:** pass an `engine` (from
 * `useMagnus`/`useAgent`) when the host layers its own orchestration on
 * top — the choice is fixed for the life of the component.
 */
/**
 * Props only the block's own engine can honor. Passing one alongside
 * `engine` is a silent no-op at runtime, so say so in development — the
 * failure otherwise looks like a broken callback rather than a mode mismatch.
 */
const SELF_DRIVING_ONLY_PROPS = ["activeStreamId", "initialMessages", "onFinish", "onFirstStream", "onStop", "turnOptions"] as const satisfies readonly (keyof MagnusChatProps)[];

/** Warned combinations, so the notice doesn't repeat on every render. */
const warnedControlledProps = new Set<string>();
function warnIgnoredControlledProps(props: MagnusChatProps) {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  const ignored = SELF_DRIVING_ONLY_PROPS.filter(key => props[key] !== undefined);
  if (ignored.length === 0) {
    return;
  }
  const key = ignored.join(",");
  if (warnedControlledProps.has(key)) {
    return;
  }
  warnedControlledProps.add(key);
  const [first] = ignored;
  console.warn(`<MagnusChat>: ${key} ${ignored.length === 1 ? "is" : "are"} ignored when the host owns the \`engine\` — pass ${ignored.length === 1 ? "it" : "them"} to the engine hook instead (e.g. \`useMagnus({ ${first} })\`).`);
}

/**
 * One pill per call: in the chat it is bound to, or — for a refused start
 * that never bound — in the chat it came from.
 */
function showLiveCallPill(liveCall: MagnusLiveCallSource, chatId: string, readOnly: boolean): boolean {
  if (readOnly) {
    return false;
  }
  return liveCall.callChatId === null ? liveCall.error !== null && (liveCall.errorChatId ?? chatId) === chatId : liveCall.callChatId === chatId;
}
export function MagnusChat(props) {
  const $ = _c(13);
  if (props.engine) {
    warnIgnoredControlledProps(props);
    let chatId;
    let engine;
    let rest;
    if ($[0] !== props) {
      ({
        engine,
        chatId,
        ...rest
      } = props);
      $[0] = props;
      $[1] = chatId;
      $[2] = engine;
      $[3] = rest;
    } else {
      chatId = $[1];
      engine = $[2];
      rest = $[3];
    }
    const t0 = chatId ?? "magnus-chat";
    let t1;
    if ($[4] !== engine || $[5] !== rest || $[6] !== t0) {
      t1 = <ChatSession {...rest} chatId={t0} engine={engine} />;
      $[4] = engine;
      $[5] = rest;
      $[6] = t0;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    let t2;
    if ($[8] !== props.chiclets || $[9] !== t1) {
      t2 = <ChicletRegistryProvider chiclets={props.chiclets}>{t1}</ChicletRegistryProvider>;
      $[8] = props.chiclets;
      $[9] = t1;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  let t0;
  if ($[11] !== props) {
    t0 = <SelfDrivingMagnusChat {...props} />;
    $[11] = props;
    $[12] = t0;
  } else {
    t0 = $[12];
  }
  return t0;
}

/** Self-driving wrapper: creates a chat id when none is given, then hands
 *  off to the engine-owning session. */
function SelfDrivingMagnusChat(t0) {
  const $ = _c(25);
  let chiclets;
  let className;
  let providedChatId;
  let rest;
  if ($[0] !== t0) {
    ({
      chatId: providedChatId,
      chiclets,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = chiclets;
    $[2] = className;
    $[3] = providedChatId;
    $[4] = rest;
  } else {
    chiclets = $[1];
    className = $[2];
    providedChatId = $[3];
    rest = $[4];
  }
  const {
    client
  } = useMagnusClient();
  const [createdChatId, setCreatedChatId] = useState(null);
  const [createError, setCreateError] = useState(null);
  const requestedRef = useRef(false);
  let t1;
  let t2;
  if ($[5] !== client || $[6] !== providedChatId) {
    t1 = () => {
      if (providedChatId || requestedRef.current) {
        return;
      }
      requestedRef.current = true;
      client.chats.create().then(chat => setCreatedChatId(chat.id)).catch(err => {
        setCreateError(err instanceof Error ? err : new Error(String(err)));
      });
    };
    t2 = [providedChatId, client];
    $[5] = client;
    $[6] = providedChatId;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  const chatId = providedChatId ?? createdChatId;
  if (createError) {
    let t3;
    if ($[9] !== className) {
      t3 = cn("flex h-full items-center justify-center", className);
      $[9] = className;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== createError.message) {
      t4 = <p className="text-destructive text-sm">Could not start a chat: {createError.message}</p>;
      $[11] = createError.message;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t3 || $[14] !== t4) {
      t5 = <div className={t3}>{t4}</div>;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  if (!chatId) {
    let t3;
    if ($[16] !== className) {
      t3 = <ChatHydrationSkeleton className={className} />;
      $[16] = className;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    return t3;
  }
  let t3;
  if ($[18] !== chatId || $[19] !== className || $[20] !== rest) {
    t3 = <SelfDrivingSession {...rest} chatId={chatId} className={className} key={chatId} />;
    $[18] = chatId;
    $[19] = className;
    $[20] = rest;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== chiclets || $[23] !== t3) {
    t4 = <ChicletRegistryProvider chiclets={chiclets}>{t3}</ChicletRegistryProvider>;
    $[22] = chiclets;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}

/** Owns the internally-created engine (`useMagnus`) and the client-tool
 *  watcher, then renders the shared session body. */
function SelfDrivingSession({
  activeStreamId,
  chatId,
  initialMessages,
  onFinish,
  onFirstStream,
  onStop,
  turnOptions,
  ...rest
}: MagnusChatProps & {
  chatId: string;
}) {
  // Read the latest options at send time without re-memoizing the transport.
  const turnOptionsRef = useRef(turnOptions);
  turnOptionsRef.current = turnOptions;
  const getBody = useCallback(() => turnOptionsRef.current ?? {}, []);
  const engine = useMagnus({
    activeStreamId,
    chatId,
    getBody,
    initialMessages,
    onFinish,
    onFirstStream,
    onStop
  });

  // Execute client tools registered on `<MagnusProvider tools={...}>`
  // (`defineClientTool`): watches the stream for server-emitted
  // `data-client-tool-request` parts, runs the matching executor, and POSTs
  // the result back so the paused turn resumes. No-op when no tools are
  // registered.
  const {
    activeTools
  } = useMagnusTools(engine.messages);
  return <ChatSession {...rest} activeTools={activeTools} chatId={chatId} engine={engine} />;
}
type ActiveTools = ReturnType<typeof useMagnusTools>["activeTools"];

/** The shared render body — engine + chatId are guaranteed here. */
function ChatSession({
  activeTools,
  approvals,
  artifactMentionOptions,
  artifacts,
  attachmentStatuses,
  banner,
  chatId,
  children,
  className,
  composer,
  composerDocked = false,
  composerDockClassName,
  composerRef,
  dictation,
  disclaimer,
  emptyState,
  enableMessageQueue = true,
  messageQueuePaused = false,
  engine,
  feedback,
  fetchToolOutput,
  hasPendingInitialSend = false,
  header,
  history,
  linkComponent,
  liveCall,
  liveCallPill = true,
  models,
  onAttachmentActivate,
  onRetryProcessing,
  onSubmit: providedOnSubmit,
  placeholder: providedPlaceholder,
  readOnly = false,
  readOnlyPlaceholder = "This conversation is read-only",
  renderError,
  renderMessageDebug,
  renderMessageHeader,
  renderMessageMenuLabel,
  renderMessageMeta,
  renderUserMessage,
  send: providedSend,
  skillMentionOptions,
  skills,
  toolViews,
  uploads
}: MagnusChatProps & {
  chatId: string;
  engine: UseMagnusResult;
  activeTools?: ActiveTools;
}) {
  const {
    messages,
    status,
    stop,
    error,
    sendMessage,
    setMessages,
    smoothTextEnabled
  } = engine;
  const {
    builtInTools
  } = useMagnusClient();
  const resolvedToolViews = useMemo(() => ({
    ...resolveToolRegistry(DEFAULT_TOOL_VIEWS, builtInTools),
    ...toolViews
  }), [builtInTools, toolViews]);

  // Default submit: the user turn is added optimistically; resolving now
  // lets the composer clear immediately. Send failures surface through
  // `engine.error`.
  const defaultSubmit = useCallback<MagnusChatSubmit>(parts => {
    if (parts.length === 0) {
      return false;
    }
    sendMessage({
      role: "user",
      parts
    }).catch(() => undefined);
    return true;
  }, [sendMessage]);
  const onSubmit = providedOnSubmit ?? defaultSubmit;
  const liveCallSubmit = useCallback<LiveCallChatSubmit>((parts_0, {
    messageId,
    voiceSessionId,
    voiceCallContext
  }) => sendAccepted(sendMessage({
    id: messageId,
    role: "user",
    parts: parts_0,
    ...(voiceSessionId ? {
      metadata: {
        voiceSessionId,
        ...(voiceCallContext ? {
          voiceCallContext
        } : {})
      }
    } : {})
  })), [sendMessage]);
  const upsertRecap = useCallback((message: MagnusUIMessage) => setMessages(prev => prev.some(m_0 => m_0.id === message.id) ? prev.map(m => m.id === message.id ? message : m) : [...prev, message]), [setMessages]);
  const removeRecap = useCallback((messageId_0: string) => setMessages(prev_0 => prev_0.filter(m_1 => m_1.id !== messageId_0)), [setMessages]);
  const liveCallBridge = useLiveCallChatBridge({
    chatId,
    status,
    messages,
    submit: liveCallSubmit,
    stop,
    upsertRecap,
    removeRecap,
    call: liveCall && liveCall.callChatId === chatId ? {
      sessionId: liveCall.sessionId,
      pushContext: liveCall.pushContext
    } : null
  });
  const registerLiveCallChat = readOnly ? undefined : liveCall?.registerChat;
  useEffect(() => registerLiveCallChat?.(liveCallBridge), [registerLiveCallChat, liveCallBridge]);
  const queueOn = enableMessageQueue && !readOnly;
  const queuedItems = useFollowUpQueue(chatId);
  const drainLocked = useDrainLocked(chatId);
  const editSlot = useFollowUpEditSlot(chatId);
  const declinedIdRef = useRef<string | null>(null);
  // Which chat's drain lock this session holds, if any. Not a boolean: in
  // controlled mode `chatId` can change under a mounted session (the block
  // documents that it doesn't remount), so the release has to name the chat
  // it acquired rather than whatever is current by then.
  const ownedDrainRef = useRef<string | null>(null);
  // This session's identity as a lock holder, so its releases can't free a
  // lock another session took after it.
  const drainOwnerRef = useRef({});
  const mountedRef = useRef(false);
  const editorHandleRef = useRef<RichComposerHandle | null>(null);
  const onValueChangeRef = useRef(composer?.onValueChange);
  onValueChangeRef.current = composer?.onValueChange;
  const setComposerRef = useCallback((handle: RichComposerHandle | null) => {
    editorHandleRef.current = handle;
    if (typeof composerRef === "function") {
      composerRef(handle);
    } else if (composerRef) {
      composerRef.current = handle;
    }
  }, [composerRef]);
  const releaseDrain = useCallback((id: string, force = false) => {
    if (ownedDrainRef.current === id) {
      ownedDrainRef.current = null;
    }
    unlockDrain(id, force ? undefined : drainOwnerRef.current);
  }, []);
  const loadComposerFromParts = useCallback((parts_1: EditorPart[]) => {
    const prompt = partsToPromptString(parts_1);
    editorHandleRef.current?.setContentFromPrompt(prompt);
    onValueChangeRef.current?.(prompt);
    editorHandleRef.current?.focus();
  }, []);
  const handleQueue = useCallback((parts_2: EditorPart[]): FollowUpQueueResult => {
    const slot = takeFollowUpEditSlot(chatId);
    if (slot) {
      const merged = mergeFollowUpParts(parts_2, slot.parts);
      if (merged.length === 0) {
        // Clearing the whole draft while editing is how you delete a queued
        // item, so say so — reporting it as queued sends a screen reader
        // looking for a row that no longer exists.
        return "removed";
      }
      insertFollowUpAt(chatId, slot.index, {
        id: slot.id,
        parts: merged
      });
      return "queued";
    }
    return enqueueFollowUp(chatId, parts_2) === null ? "rejected" : "queued";
  }, [chatId]);
  useEffect(() => {
    return () => {
      const slot_0 = takeFollowUpEditSlot(chatId);
      if (slot_0) {
        insertFollowUpAt(chatId, slot_0.index, {
          id: slot_0.id,
          parts: slot_0.parts
        });
      }
    };
  }, [chatId]);
  const sendText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    Promise.resolve(onSubmit([{
      type: "text",
      text: trimmed
    }])).catch(() => undefined);
  }, [onSubmit]);

  // Lets action chiclets (prompt-link) send follow-ups into this chat, same
  // The one plain-text send seam, shared by the chiclets and the host's
  // empty-branch `sendText` so `send` overrides both (as its doc says) — a
  // host that pins the URL on the first send needs the hero's suggestion
  // chips going through the same door as its composer. Read-only viewers
  // can't send at all; the composer is already disabled for them, and this
  // keeps a host-rendered chip from walking into a 403.
  const plainTextSend = useCallback((text_0: string) => {
    if (readOnly) {
      return;
    }
    (providedSend ?? sendText)(text_0);
  }, [providedSend, readOnly, sendText]);

  // as the web app's chat context.
  const chicletActions = useMemo(() => ({
    canSend: status === "ready" && !readOnly,
    send: plainTextSend
  }), [status, readOnly, plainTextSend]);
  const [dockRef, dockHeight] = useElementHeight<HTMLDivElement>();

  // Attachment staging lives here, above the empty/active swap and the C17
  // prompt replacement, because both unmount the composer. The hook's unmount
  // sweep deletes finalized upload rows it reads as abandoned, which would
  // otherwise destroy the rows the just-sent message references.
  const onProcessingSettledRef = useRef(composer?.onAttachmentProcessingSettled);
  onProcessingSettledRef.current = composer?.onAttachmentProcessingSettled;
  const onProcessingFailedRef = useRef(composer?.onAttachmentProcessingFailed);
  onProcessingFailedRef.current = composer?.onAttachmentProcessingFailed;
  const attachments = useAttachments(uploads, {
    onProcessingSettled: (uploadId, fileName, failed) => {
      onProcessingSettledRef.current?.(uploadId);
      if (failed) {
        onProcessingFailedRef.current?.(uploadId, fileName);
      }
    }
  });
  const collectComposerParts = useCallback((): EditorPart[] => {
    const parts_3 = editorHandleRef.current?.getParts() ?? [];
    // Unlike a send, this runs with no `canSend` gate behind it, so a tile
    // still uploading can be staged here. Skip those the way the composer's
    // own `uploadedFilesToSend` does — `toAttachmentFilePart` throws on them.
    const fileParts = attachments.uploadedFiles.flatMap(file => file.uploaded ? [toAttachmentFilePart(file)] : []);
    return [...parts_3, ...fileParts];
  }, [attachments.uploadedFiles]);
  const clearComposer = useCallback(() => {
    editorHandleRef.current?.clear();
    onValueChangeRef.current?.("");
    attachments.clearFiles();
  }, [attachments]);
  const handleEditQueued = useCallback((id_0: string) => {
    const draft = collectComposerParts();
    const hadSlot = Boolean(getFollowUpEditSlot(chatId));
    if (hadSlot) {
      handleQueue(draft);
    }
    const slot_1 = beginFollowUpEdit(chatId, id_0);
    if (!slot_1) {
      return;
    }
    // Pull the row out first so a full queue still has room for the
    // composer's current draft. Enqueue-before-remove dropped that draft.
    if (!hadSlot && draft.length > 0) {
      enqueueFollowUp(chatId, draft);
    }
    attachments.clearFiles();
    // Text only: the row's file parts stay parked in the slot and are
    // merged back on submit. Re-staging them as tiles would need the
    // original `File` objects, which are gone once an upload finalizes —
    // so an edited row shows no sign of its attachments until it re-queues.
    loadComposerFromParts(slot_1.parts);
  }, [attachments, chatId, collectComposerParts, handleQueue, loadComposerFromParts]);
  const handleCancelQueueEdit = useCallback(() => {
    const slot_2 = takeFollowUpEditSlot(chatId);
    if (!slot_2) {
      return;
    }
    insertFollowUpAt(chatId, slot_2.index, {
      id: slot_2.id,
      parts: slot_2.parts
    });
    clearComposer();
    editorHandleRef.current?.focus();
  }, [chatId, clearComposer]);
  const isEmpty = messages.length === 0 && !hasPendingInitialSend;
  const renderEmpty = typeof emptyState === "function" ? emptyState : undefined;
  const staticEmptyState = typeof emptyState === "function" ? undefined : emptyState;
  const placeholder = readOnly ? readOnlyPlaceholder : providedPlaceholder;

  // Interactive prompts replace the composer: a host replacement first, then
  // the block's own multiple-choice picker. Read-only viewers can't answer.
  const multipleChoice = useMemo(() => readOnly ? null : getActiveMultipleChoice(messages, status), [messages, readOnly, status]);
  const activeApproval = useApprovalPrompt({
    messages,
    source: approvals
  });
  const approval = readOnly ? null : activeApproval;
  const errorBanner = useTransportErrorBanner({
    error,
    messages
  });
  // Sent-attachment processing (plan 043 P8): a host map wins outright —
  // its owner polls and retries on its own — otherwise the block watches
  // sent uploads through the same `uploads` source the composer stages with,
  // so `useMagnusConversation().chatProps` lights up the live dots and the
  // retry with no extra host code.
  const sourceStatuses = useMagnusAttachmentStatuses(messages, attachmentStatuses ? undefined : uploads);
  const resolvedAttachmentStatuses = attachmentStatuses ?? sourceStatuses.attachmentStatuses;
  const resolvedRetryProcessing = onRetryProcessing ?? (attachmentStatuses ? undefined : sourceStatuses.onRetryProcessing);

  // Attachment preview (plan 043 P9): the default click for staged and sent
  // tiles when the host passes no `onAttachmentActivate`. Sent tiles reach it
  // through context (the transcript rows are memoized and deep), staged
  // tiles through the composer's activation prop below.
  const [previewTarget, setPreviewTarget] = useState<AttachmentPreviewTarget | null>(null);
  const resolveUploadUrl = uploads?.resolveUrl;
  const attachmentPreview = useMemo(() => ({
    open: setPreviewTarget,
    resolveUrl: resolveUploadUrl
  }), [resolveUploadUrl]);
  const closePreview = useCallback(() => setPreviewTarget(null), []);
  // A staged tile previews from the local File — no round trip, and it works
  // before the upload finalizes. Types the dialog can't render open the
  // finalized URL in a new tab instead, like a sent file part does.
  const previewStagedAttachment = useCallback((attachment: ComposerAttachment) => {
    const {
      file: file_0
    } = attachment;
    if (isPreviewableMediaType(file_0.type)) {
      setPreviewTarget({
        file: file_0,
        mediaType: file_0.type,
        name: file_0.name
      });
      return;
    }
    const url = attachment.uploaded?.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);
  const defaultRenderError = useCallback((transportError: Error) => errorBanner.visible ? <TransportErrorBanner error={transportError} onDismissError={errorBanner.dismiss} /> : null, [errorBanner.dismiss, errorBanner.visible]);
  let promptReplacement: ReactNode = null;
  if (composer?.promptReplacement && composer.renderPromptReplacement) {
    promptReplacement = composer.renderPromptReplacement(composer.promptReplacement.kind, composer.promptReplacement.payload);
  } else if (approval) {
    promptReplacement = <div className="relative">
        <ApprovalInput copy={approval.copy} key={approval.toolCallId} onResolve={approval.onResolve} request={approval.request} uploads={uploads} />
      </div>;
  } else if (multipleChoice) {
    promptReplacement = <div className="relative">
        <MultipleChoiceInput key={multipleChoice.key} onSkip={() => sendText(MULTIPLE_CHOICE_SKIP_MESSAGE)} onSubmit={sendText} prompts={multipleChoice.prompts}
      // Block Submit/Skip until the turn finishes streaming, so an
      // answer can't fire a user message mid-stream.
      submitDisabled={!multipleChoice.canSubmit} />
      </div>;
  }
  const promptHold = Boolean(composer?.promptReplacement) || approval !== null || multipleChoice !== null;

  // Approval / multiple-choice replaces the composer. Put the in-edit
  // follow-up back so it isn't stranded in a slot with no editor.
  useEffect(() => {
    if (!promptHold) {
      return;
    }
    const slot_3 = takeFollowUpEditSlot(chatId);
    if (!slot_3) {
      return;
    }
    insertFollowUpAt(chatId, slot_3.index, {
      id: slot_3.id,
      parts: slot_3.parts
    });
  }, [chatId, promptHold]);
  useEffect(() => {
    if (status === "submitted" || status === "streaming") {
      // Unconditional, not ownership-scoped: a turn running means any lock on
      // this chat is stale, whoever left it there.
      releaseDrain(chatId, true);
      declinedIdRef.current = null;
      return;
    }
    if (messageQueuePaused) {
      // A temporary host hold may have rejected the head before this render.
      // Retry it when the hold clears, without retrying ordinary declines.
      declinedIdRef.current = null;
      return;
    }
    if (!queueOn || promptHold || queuedItems.length === 0) {
      return;
    }
    // `error` counts as idle on purpose: a queued follow-up is often how a
    // user retries a turn that just failed, and holding the queue would
    // strand it with no resume affordance. The cost is that a backend down
    // hard walks the whole queue, one item per failed turn — visible in the
    // transcript, and the rows stay deletable while it happens.
    if (status !== "ready" && status !== "error") {
      return;
    }
    if (queuedItems[0]?.id === declinedIdRef.current) {
      return;
    }
    // Re-runs when the lock frees, so a session that mounted while a
    // previous one's late release was pending still drains.
    if (drainLocked || !tryLockDrain(chatId, drainOwnerRef.current)) {
      return;
    }
    ownedDrainRef.current = chatId;
    const head = takeFollowUp(chatId);
    if (!head) {
      releaseDrain(chatId);
      return;
    }
    Promise.resolve(onSubmit(head.parts)).then(accepted => {
      if (accepted === false) {
        declinedIdRef.current = head.id;
        unshiftFollowUp(chatId, head);
        releaseDrain(chatId);
      }
    }).catch(() => {
      declinedIdRef.current = head.id;
      unshiftFollowUp(chatId, head);
      releaseDrain(chatId);
    });
  }, [status, chatId, queueOn, messageQueuePaused, promptHold, onSubmit, queuedItems, drainLocked, releaseDrain]);

  // The drain lock is module state so a Strict Mode double-invoke (or a fast
  // remount) can't send the same head twice. The flip side: unmounting
  // mid-drain — navigating away as a queued send fires — leaves it held with
  // nothing left to run the release, and every later mount finds the queue
  // locked and quietly stops draining. Release it on the way out, but a tick
  // late and only if this session holds it: Strict Mode runs this cleanup
  // *between* two setups, so releasing synchronously would hand the second
  // pass a free lock and drain a second item.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      setTimeout(() => {
        const owned = ownedDrainRef.current;
        if (mountedRef.current || owned === null) {
          return;
        }
        releaseDrain(owned);
      }, 0);
    };
  }, [releaseDrain]);
  const docked = composerDocked && !isEmpty;
  const flow = !(docked || isEmpty && renderEmpty);
  // The dock supplies the composer's bottom inset (`pb-6`, sized for the
  // disclaimer that hangs below it). The composer's own `pb-4`, meant for the
  // hero layout where nothing else pads it, would stack on top and float the
  // composer well off the bottom edge — most obvious on mobile. In flow (the
  // floating panel) the composer sits against the container's edges, so its
  // bottom inset matches its `px-2` sides.
  const composerPadding = docked ? "pb-0" : flow ? "pb-2" : undefined;
  const composerNode = promptReplacement ?
  // C17: approval / multiple-choice prompts replace the composer. The
  // wrapper div keeps the flow layout's composer footprint.
  <div className={cn(
  // Same container-driven inset as the composer it stands in for — see
  // the note on RichComposer's root.
  "mx-auto w-full max-w-3xl px-2 pb-4 @[50rem]:px-0", composerPadding)}>
      {promptReplacement}
    </div> : <RichComposer actionsMenuExtras={composer?.actionsMenuExtras} artifactMentionOptions={artifactMentionOptions} artifacts={artifacts} attachments={attachments}
  // Branch-aware default: the empty hero focuses on mount; the docked
  // active composer doesn't yank focus from the transcript (flagship
  // behavior).
  autoFocus={composer?.autoFocus ?? isEmpty} className={composerPadding} dictation={dictation} disabled={readOnly} enableMessageQueue={queueOn} extras={composer?.extras} headerSlot={composer?.headerSlot} initialSkill={composer?.initialSkill} leftTools={composer?.leftTools} liveCall={readOnly ? undefined : liveCall} models={models} notifyBell={composer?.notifyBell} onAttachmentActivate={composer?.onAttachmentActivate ?? previewStagedAttachment} onCancelQueueEdit={editSlot ? handleCancelQueueEdit : undefined} onQueue={handleQueue} onSend={onSubmit} onSkillsChange={composer?.onSkillsChange} onStop={stop} onValueChange={composer?.onValueChange} placeholder={placeholder} preferQueue={Boolean(editSlot)} ref={setComposerRef} renderRightCluster={composer?.renderRightCluster} renderSkillHover={composer?.renderSkillHover} rightTools={composer?.rightTools} skillMentionOptions={skillMentionOptions} skills={skills} status={status} submitDisabled={composer?.submitDisabled} uploads={uploads} value={composer?.value} />;
  const composerColumn = <>
      {liveCall && liveCallPill && showLiveCallPill(liveCall, chatId, readOnly) && <div className="pointer-events-none flex justify-center px-2 pb-2 empty:hidden">
            <LiveCallPill call={liveCall} captions="inline" />
          </div>}
      {queueOn ? <FollowUpQueue chatId={chatId} onDeleteUploads={uploads ? ids => {
      for (const id_1 of ids) {
        uploads.delete(id_1).catch(() => undefined);
      }
    } : undefined} onEdit={handleEditQueued} /> : null}
      {composerNode}
    </>;
  const transcriptNode =
  // Restores expand-on-click for collapsible tool rows (the plan-030
  // frozen-expandable gap): without this context, `useToolAutoOpen`
  // discards manual toggles and every tool stays frozen shut.
  <ToolViewProvider>
      <Transcript attachmentStatuses={resolvedAttachmentStatuses} banner={banner} bottomPad={docked ? dockHeight + 32 : undefined} emptyState={staticEmptyState} error={error} feedback={feedback} linkComponent={linkComponent} messages={messages} onAttachmentActivate={onAttachmentActivate} onRetryProcessing={resolvedRetryProcessing} renderError={renderError ?? defaultRenderError} renderMessageDebug={renderMessageDebug} renderMessageHeader={renderMessageHeader} renderMessageMenuLabel={renderMessageMenuLabel} renderMessageMeta={renderMessageMeta ?? renderUserMessageTimestamp} renderUserMessage={renderUserMessage} scrollButtonOffset={docked ? dockHeight + 16 : undefined} smoothTextEnabled={smoothTextEnabled} status={status} />
    </ToolViewProvider>;
  let body: ReactNode;
  if (isEmpty && renderEmpty) {
    // Host empty layout (the flagship hero): the function owns the whole
    // branch and embeds the composer node where it wants it.
    body =
    // @container: the composer's horizontal inset queries the chat column's
    // width (RichComposer root). All three body branches declare it so the
    // composer measures the same box wherever it mounts. Safe here: no
    // non-portaled `position:fixed` content renders inside, and this div is
    // already `relative`, so the implied `contain: layout` re-anchors
    // nothing.
    <div className="@container relative flex min-h-0 flex-1 flex-col">
        {renderEmpty({
        composer: composerColumn,
        messages,
        sendText: plainTextSend,
        status
      })}
      </div>;
  } else if (docked) {
    // Flagship active layout: transcript scrolls under a measured,
    // absolutely-docked composer (the padding keeps the last message clear).
    body = <div className="relative flex w-full min-w-0 flex-1 flex-col overflow-hidden">
        {header}
        {transcriptNode}
        {/* Opaque backdrop for the dock. The composer frame is translucent in
            dark mode (`dark:bg-input/30` lifts it off the page), so it needs a
            solid surface behind it or the transcript scrolls through the
            composer. Sized to the measured dock rather than a fixed height so
            a grown composer — attachments, a multi-line draft — can't outrun
            it. `h-24` covers the first paint, before the dock is measured. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 mx-auto h-24 max-w-3xl bg-background" style={dockHeight ? {
        height: dockHeight
      } : undefined} />
        <div className={cn(
      // @container makes the dock the composer's width reference —
      // `inset-x-0` gives it the column's full width, which is what the
      // inset query on RichComposer's root needs to measure (the inner
      // max-w-3xl wrapper is capped, so it can never report "wide").
      "@container pointer-events-none absolute inset-x-0 bottom-0 z-20",
      // pb-6 (not pb-4) leaves room for the disclaimer, which hangs
      // below the composer at `-bottom-5.25`; less padding clips its
      // last line. The horizontal inset is left to the composer's own
      // container-queried `px-2` so it tracks the column at every width.
      "pb-6", composerDockClassName)} ref={dockRef}>
          <div className="pointer-events-auto relative mx-auto w-full max-w-3xl">
            {composerColumn}
            {disclaimer && <p className="-bottom-5.25 absolute inset-x-0 truncate px-2 text-center text-[8px] text-muted-foreground/60 sm:text-[10px] md:text-[12px]">
                {disclaimer}
              </p>}
          </div>
        </div>
        {children}
      </div>;
  } else {
    // Flow layout (`/demo`, embeds, the floating panel): transcript, then
    // composer in normal flow. Header mounts above the transcript when
    // supplied. The bottom inset is `composerPadding`'s pb-2.
    body = <>
        {header}
        {transcriptNode}
        <div className="@container">{composerColumn}</div>
        {children}
      </>;
  }
  return <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {history && <div className="flex shrink-0 items-center border-b px-2 py-1">
          <ChatHistorySwitcher activeChatId={chatId} history={history} />
        </div>}
      <ChicletChatActionsProvider value={chicletActions}>
        <ToolViewsProvider toolViews={resolvedToolViews}>
          <LazyToolOutputProvider fetchToolOutput={fetchToolOutput}>
            <AttachmentPreviewProvider value={attachmentPreview}>
              {activeTools ? <ClientToolProvider activeTools={activeTools}>
                  {body}
                </ClientToolProvider> : body}
            </AttachmentPreviewProvider>
          </LazyToolOutputProvider>
        </ToolViewsProvider>
      </ChicletChatActionsProvider>
      <AttachmentPreviewDialog onClose={closePreview} target={previewTarget} />
    </div>;
}